import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchSystemReports } from '../services/api';
import { SystemInfo } from '../types';

const EXCLUDED_DIFF_KEYS = new Set(['id', 'timestamp', '_rid', '_self', '_etag', '_attachments', '_ts']);
const CHANGE_PREVIEW_LIMIT = 3;
const ENGLISH_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

type ChangeKind = 'added' | 'removed' | 'changed';

type ChangedField = {
    key: string;
    value: unknown;
    kind: ChangeKind;
};

const parseTimestamp = (ts: string): Date => {
    // Cosmos returns up to 7 fractional second digits; JS Date only handles 3.
    const normalized = ts.replace(/(\.\d{3})\d+/, '$1');
    return new Date(normalized);
};

const formatReportDateTime = (timestamp: string): string => {
    const date = parseTimestamp(timestamp);

    if (Number.isNaN(date.getTime())) {
        return timestamp;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = ENGLISH_MONTHS[date.getMonth()] ?? '';
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day} ${month} ${year} - ${hours}:${minutes}`;
};

const formatValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === '';

const getChangedFields = (current: SystemInfo, previous?: SystemInfo): ChangedField[] => {
    if (!previous) {
        return Object.entries(current)
            .filter(([key]) => !EXCLUDED_DIFF_KEYS.has(key))
            .map(([key, value]) => ({ key, value, kind: 'added' }));
    }

    const keys = new Set<string>([
        ...Object.keys(current),
        ...Object.keys(previous),
    ]);

    return Array.from(keys)
        .filter((key) => !EXCLUDED_DIFF_KEYS.has(key))
        .filter((key) => JSON.stringify(current[key]) !== JSON.stringify(previous[key]))
        .map((key) => {
            const currentValue = current[key];
            const previousValue = previous[key];

            if (isEmptyValue(previousValue) && !isEmptyValue(currentValue)) {
                return { key, value: currentValue, kind: 'added' as const };
            }

            if (!isEmptyValue(previousValue) && isEmptyValue(currentValue)) {
                return { key, value: currentValue, kind: 'removed' as const };
            }

            return { key, value: currentValue, kind: 'changed' as const };
        });
};

const toMonthLabel = (timestamp: string): string => {
    const date = parseTimestamp(timestamp);

    if (Number.isNaN(date.getTime())) {
        return timestamp;
    }

    const month = ENGLISH_MONTHS[date.getMonth()] ?? '';
    const year = date.getFullYear();

    return `${month} ${year}`;
};

const ReportDetails: React.FC = () => {
    const { serialNumber } = useParams<{ serialNumber: string }>();
    const [reports, setReports] = React.useState<SystemInfo[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const getReportDetails = async () => {
            try {
                const data = await fetchSystemReports(serialNumber, 500);
                setReports(data);
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch report details';
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        if (serialNumber) {
            getReportDetails();
        }
    }, [serialNumber]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    if (reports.length === 0) {
        return <div>No reports found for serial number: {serialNumber}</div>;
    }

    const sortedReports = [...reports].sort(
        (a, b) => parseTimestamp(b.timestamp).getTime() - parseTimestamp(a.timestamp).getTime()
    );

    const reportsByMonth = sortedReports.reduce<Array<{ label: string; items: Array<{ report: SystemInfo; index: number }> }>>((groups, report, index) => {
        const label = toMonthLabel(report.timestamp);
        const existingGroup = groups[groups.length - 1];

        if (!existingGroup || existingGroup.label !== label) {
            groups.push({
                label,
                items: [{ report, index }],
            });
            return groups;
        }

        existingGroup.items.push({ report, index });
        return groups;
    }, []);

    return (
        <div className="dashboard reports-page">
            <div className="reports-page-header">
                <h2>Reports for Serial Number: {serialNumber}</h2>
                <p className="summary">Latest first. Click a report card or the action to open full JSON.</p>
                <div className="reports-legend" role="note" aria-label="Reports legend">
                    <span className="reports-legend-item"><strong>{sortedReports.length}</strong> reports</span>
                    <span className="reports-legend-item">Changes are compared with the previous report</span>
                    <span className="reports-legend-item">Newest report is marked as Latest</span>
                </div>
            </div>

            {reportsByMonth.map((monthGroup) => (
                <section key={monthGroup.label} className="reports-month-section">
                    <h3 className="reports-month-title">{monthGroup.label}</h3>
                    <div className="reports-timeline">
                        {monthGroup.items.map(({ report, index }) => {
                            const olderReport = index < sortedReports.length - 1 ? sortedReports[index + 1] : undefined;
                            const changedFields = getChangedFields(report, olderReport);
                            const previewFields = changedFields.slice(0, CHANGE_PREVIEW_LIMIT);
                            const moreChanges = Math.max(changedFields.length - previewFields.length, 0);
                            const isLatest = index === 0;
                            const isFirstRegistration = index === sortedReports.length - 1;

                            return (
                                <Link
                                    key={report.id}
                                    className="report-link report-timeline-link"
                                    to={`/report/${encodeURIComponent(serialNumber || '')}/${encodeURIComponent(report.id)}`}
                                >
                                    <article className="report-card report-timeline-card">
                                        <div className="report-card-top-row">
                                            <div>
                                                <p className="report-card-kicker">Report {index + 1} of {sortedReports.length}</p>
                                                <h4 className="report-card-time">{formatReportDateTime(report.timestamp)}</h4>
                                            </div>
                                            <div className="report-card-badges">
                                                {isLatest && <span className="report-badge report-badge-latest">Latest</span>}
                                                {isFirstRegistration && <span className="report-badge report-badge-first">First registration</span>}
                                                <span className="report-badge report-badge-changes">{changedFields.length} changes</span>
                                            </div>
                                        </div>

                                        <p className="report-comparison-note">
                                            {isFirstRegistration
                                                ? 'This is the first known registration snapshot.'
                                                : 'Difference compared with previous report.'}
                                        </p>

                                        <div className="report-details report-details-preview">
                                            {changedFields.length === 0 && (
                                                <p>No changes compared to the previous registration.</p>
                                            )}
                                            {previewFields.map((field) => (
                                                <p key={field.key}>
                                                    <span className="report-change-content">
                                                        <strong>{field.key}:</strong>{' '}
                                                        <span className="report-change-value">{formatValue(field.value)}</span>
                                                    </span>
                                                    <span className={`report-change-pill report-change-pill-${field.kind}`}>{field.kind}</span>
                                                </p>
                                            ))}
                                            {moreChanges > 0 && (
                                                <p className="report-more-changes">+{moreChanges} more changes</p>
                                            )}
                                        </div>

                                        <div className="report-card-footer">
                                            <span className="report-card-cta">View full JSON <span aria-hidden="true">&rarr;</span></span>
                                        </div>
                                    </article>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            ))}
        </div>
    );
};

export default ReportDetails;